import { ReactNode, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AiOutlineBarChart, AiOutlineDoubleLeft, AiOutlineDoubleRight, AiOutlineHome } from "react-icons/ai";
import { AiOutlineSetting } from "react-icons/ai";
import { AiOutlineUser } from "react-icons/ai";
import { IoLogOutOutline, IoTicketOutline } from "react-icons/io5";
import { HiOutlineBookOpen } from "react-icons/hi";
import toast from "react-hot-toast";
import Tooltip from "@mui/joy/Tooltip";
import { ApiUpdateEntity, RefreshCache } from "@/legacy/ApiCallerOld";
import { useAgent } from "@/Hooks/useAgent";
import { useAppDispatch, useAppSelector } from "../../Store/hooks";
import { AgentAvatar } from "../Avatar/AgentAvatar";
import { agentLoggedIn } from "../../Store/Reducers/loggedInAgentSlice";
import { ConnectionStatuses } from "@shared/Enums/Enums";
import { Agent } from "@shared/Entities/EntityTypes";
import { Entities } from "@shared/Entities/Entities";
import Keys from "@shared/Components/Key/Keys";
import Key from "@shared/Components/Key/Key";

export default function Sidebar() {
	const agent = useAgent();
	const dispatch = useAppDispatch();
	const connectionStatusState = useAppSelector(state => state.connectionStatus);

	const [expanded, setExpanded] = useState(agent?.prefs.hideSidebar == "true" ? false : true);

	async function toggleSidebar() {
		const newExpanded = !expanded;

		// Toggle.
		setExpanded(newExpanded);

		// Update + save pref.
		if (agent != null) {
			const updatedAgent = structuredClone(agent);
			updatedAgent.prefs.hideSidebar = newExpanded ? "false" : "true";

			const resAgent = await ApiUpdateEntity<Agent>(Entities.AGENT, updatedAgent.id, updatedAgent);

			if (resAgent.successful && resAgent.data != null) {
				dispatch(agentLoggedIn(resAgent.data));

				await RefreshCache(dispatch);
			} else {
				toast.error("Error. " + resAgent.errorCode + ": " + resAgent.errorMsg);
				return;
			}
		}
	}

	let statusColor = "#FDFDFD";

	if (connectionStatusState.status == ConnectionStatuses.ORANGE) {
		statusColor = "#DCD6A8";
	} else if (connectionStatusState.status == ConnectionStatuses.RED) {
		statusColor = "#D26666";
	}

	const expandedStyles = expanded ? " w-64 " : " w-[4.5rem] ";
	const wordmarkStyles = expanded ? " opacity-100" : " opacity-0";

	return (
		<div className={"h-screen p-3 flex flex-col shrink-0 transition-[width] relative border-r border-gray-200 bg-white" + expandedStyles} >
			<div className="h-14 overflow-hidden" style={{ backgroundColor: statusColor }}>
				<Link to="/home" className="whitespace-nowrap">
					{/* <img src="/logo.png" className="w-11" />*/}
					<img src="/logo.png" className="h-11 object-cover" />
					<img src="/wordmark.png" className={"h-10 object-cover transition-opacity" + wordmarkStyles} />
				</Link>
			</div>
			<div className="flex-grow overflow-hidden">
				<SidebarButton
					expanded={expanded}
					title="Home"
					link="/home"
					icon={<AiOutlineHome />}
					tooltipShortcut={<Keys label="Home" keys={[<Key>G</Key>, "then", <Key>H</Key>]} />}
				/>
				<SidebarButton
					expanded={expanded}
					title="Tickets"
					link="/tickets"
					icon={<IoTicketOutline />}
					tooltipShortcut={<Keys label="Tickets" keys={[<Key>G</Key>, "then", <Key>T</Key>]} />}
				/>
				<SidebarButton
					expanded={expanded}
					title={"Users & Companies"}
					link="/companies"
					icon={<AiOutlineUser />}
					tooltipShortcut={<Keys label="Users & Companies" keys={[<Key>G</Key>, "then", <Key>C</Key>]} />}
				/>
				<SidebarButton
					expanded={expanded}
					title="Knowledge Base"
					link="/kb"
					icon={<HiOutlineBookOpen />}
					tooltipShortcut="Knowledge Base"
				/>
				<SidebarButton
					expanded={expanded}
					title="Reporting"
					link="/reporting"
					icon={<AiOutlineBarChart />}
					tooltipShortcut={<Keys label="Reporting" keys={[<Key>G</Key>, "then", <Key>R</Key>]} />}
				/>
			</div>
			<div>
				<button onClick={toggleSidebar} className="bg-white hover:bg-gray-50 hover:shadow-md active:bg-gray-100 border border-gray-400 rounded-full w-7 h-7 shadow  absolute flex items-center justify-center" style={{ right: "calc(-1.5rem / 2)", top: "50%", zIndex: 1 }}>
					{expanded ? <AiOutlineDoubleLeft /> : <AiOutlineDoubleRight />}
				</button>
			</div>
			<div className="">
				<div className="overflow-hidden">
					<SidebarButton
						expanded={expanded}
						title="Configuration"
						link="/config"
						icon={<AiOutlineSetting />}
						tooltipShortcut="Configuration"
					/>
				</div>
				<LoggedInAgentSection expanded={expanded} />
			</div>
		</div>
	);
}

interface SidebarButtonProps {
	icon: JSX.Element;
	title: string;
	link: string;
	expanded: boolean;
	tooltipShortcut: ReactNode;
}

function SidebarButton(props: SidebarButtonProps) {
	const navigate = useNavigate();
	const location = useLocation();

	const active = location.pathname.includes(props.link) || (props.link == "/tickets" && location.pathname == "/");

	const activeStyles = active ? " bg-blue-50 text-blue-800" : " text-gray-700 hover:bg-gray-100 active:bg-gray-200";
	const titleStyles = props.expanded ? " opacity-100" : " opacity-0";

	const button =	<button
		className={"flex items-center px-2.5 py-2.5 my-0.5 rounded w-full whitespace-nowrap" + activeStyles}
		onClick={() => navigate(props.link)}
	               >
		<div className="text-2xl mr-2 flex items-center">{props.icon}</div>
		<div className={"font-semibold transition-opacity" + titleStyles}>{props.title}</div>
	</button>;

	return (
		props.expanded ?
			button
			:
			<Tooltip title={props.tooltipShortcut} placement="right">
				{button}
			</Tooltip>
	);
}

interface LoggedInAgentSectionProps {
	expanded: boolean;
}

function LoggedInAgentSection(props: LoggedInAgentSectionProps) {
	const agent = useAgent();

	const rightStyles = props.expanded ? " opacity-100" : " opacity-0";
	const logoutStyles = props.expanded ? " " : " hidden";

	return (
		<div className="flex items-center border-t border-gray-200 pt-3 w-full">
			<Link to={"/config/manage/agents/" + agent?.id} className="grow flex hover:bg-gray-100 active:bg-gray-200 p-1 rounded min-w-0">
				<div className="mr-2">
					{agent &&
						<AgentAvatar agent={agent} widthPx={40} />}
				</div>
				<div className={"whitespace-nowrap overflow-hidden transition-opacity " + rightStyles}>
					{agent?.name}
					<br />
					<span className="text-gray-600 text-sm">{agent?.jobTitle}</span>
				</div>
			</Link>
			<Tooltip arrow title="Log out" enterDelay={500} placement="right">
				<Link to="/logout" className={"shrink-0 transition-opacity  " + rightStyles + logoutStyles}>
					<div className="text-xl rounded text-gray-600 hover:bg-gray-100 active:bg-gray-200 hover:text-gray-700 flex items-center p-1 ml-1">
						<IoLogOutOutline />
					</div>
				</Link>
			</Tooltip>
		</div>
	);
}
